const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 連線逾時設定（毫秒）。一般請求與檔案上傳分開設定，預設值在本地與正式環境完全相同，
// 僅允許透過環境變數覆寫（屬於「設定」差異，而非「機制」差異）。
const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 15000;
const UPLOAD_TIMEOUT_MS = Number(process.env.REACT_APP_UPLOAD_TIMEOUT_MS) || 120000;

class ApiClient {
  constructor() {
    this.baseURL = API_URL;
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  handleUnauthorized(endpoint) {
    const isLoginAttempt = endpoint === '/api/auth/login' || endpoint === '/api/auth/register';
    if (isLoginAttempt) return;

    // 業務邏輯：正式環境部署或 token 過期後，舊 token 會讓受保護 API 持續回 401；此處統一清除登入狀態並通知 AuthContext。
    this.setToken(null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tech-forum:auth-unauthorized', { detail: { endpoint } }));
    }
  }

  // 對外廣播連線狀態：後端連不上（連線被拒／逾時／CORS 不通）發出 unreachable，
  // 任何一次成功取得 HTTP 回應則發出 reachable。此事件機制在本地與正式環境一致，
  // 由 ConnectionBanner 統一呈現，避免後端掛掉時前端只能無限「載入中」或狂噴 console。
  emitConnectivity(reachable) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(
      reachable ? 'tech-forum:api-reachable' : 'tech-forum:api-unreachable'
    ));
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    return headers;
  }

  async request(method, endpoint, data = null) {
    const url = `${this.baseURL}${endpoint}`;
    const isFormData = data instanceof FormData;
    const headers = {};

    // Don't set Content-Type for FormData - browser sets it with boundary
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    const hadToken = Boolean(this.token);

    const options = { method, headers };

    if (data) {
      options.body = isFormData ? data : JSON.stringify(data);
    }

    // 逾時控制：避免後端卡死時前端永遠停在「載入中」。上傳檔案給較長時間，其餘用一般時間。
    const controller = new AbortController();
    const timeoutMs = isFormData ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    options.signal = controller.signal;

    try {
      const response = await fetch(url, options);
      // 只要拿到任何 HTTP 回應（即使是 4xx/5xx）就代表後端可達，立即清除連線異常狀態。
      this.emitConnectivity(true);

      const text = await response.text();
      const result = text ? JSON.parse(text) : { success: response.ok, data: null };

      if (!response.ok) {
        if (response.status === 401 && hadToken) this.handleUnauthorized(endpoint);
        const apiError = new Error(result.error || 'API request failed');
        apiError.status = response.status;
        throw apiError;
      }

      return result;
    } catch (error) {
      // 連線層失敗的統一判定：逾時觸發 AbortError；連線被拒／CORS 不通由 fetch 拋出 TypeError。
      // 兩者都代表「後端連不上」，歸類為 isConnectivity 並廣播 unreachable，讓 UI 一致地呈現。
      const isTimeout = error.name === 'AbortError';
      const isNetwork = error.name === 'TypeError';
      if (isTimeout || isNetwork) {
        this.emitConnectivity(false);
        const connError = new Error('無法連線到伺服器，請檢查網路連線後重新整理');
        connError.code = isTimeout ? 'TIMEOUT' : 'NETWORK';
        connError.isConnectivity = true;
        if (process.env.NODE_ENV === 'development') {
          // 僅在開發模式提示本地排查方向，不影響使用者實際看到的訊息（機制兩環境一致）。
          console.warn(`[API] 連線失敗 (${connError.code})：${url}；本地請確認 worker 是否在 8787（./local-dev.sh restart）。`);
        }
        throw connError;
      }
      if (error.status !== 401) console.error('API Error:', error);
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  get(endpoint) {
    return this.request('GET', endpoint);
  }

  post(endpoint, data) {
    return this.request('POST', endpoint, data);
  }

  put(endpoint, data) {
    return this.request('PUT', endpoint, data);
  }

  delete(endpoint) {
    return this.request('DELETE', endpoint);
  }
}

const apiClient = new ApiClient();
export default apiClient;
